import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';

// Assignment reminders for a cohort: settings, a preview of the next 72
// hours and the delivery log (communication service;
// lms_backend docs/api/assignment-reminders.md).
const base = (cohortId) => `/cohort-alerts/${cohortId}`;
const KEY = 'cohort-reminders';
export const LOG_PAGE_SIZE = 50;

const options = { refetchOnWindowFocus: false, retry: false };

const clean = (params) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  );

export const useReminderSettings = (cohortId) =>
  useQuery(
    [KEY, cohortId, 'settings'],
    async () => (await axiosInstancev2.get(`${base(cohortId)}/settings`)).data,
    { ...options, enabled: Boolean(cohortId) }
  );

export const useSaveReminderSettings = (cohortId) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (changes) =>
      (await axiosInstancev2.put(`${base(cohortId)}/settings`, changes)).data,
    {
      onSuccess: (data) => {
        queryClient.setQueryData([KEY, cohortId, 'settings'], data);
        queryClient.invalidateQueries([KEY, cohortId, 'preview']);
      },
    }
  );
};

export const useReminderPreview = (cohortId, enabled = true) =>
  useQuery(
    [KEY, cohortId, 'preview'],
    async () => (await axiosInstancev2.get(`${base(cohortId)}/preview`)).data,
    { ...options, enabled: Boolean(cohortId && enabled) }
  );

export const useReminderAssignments = (cohortId) =>
  useQuery(
    [KEY, cohortId, 'assignments'],
    async () =>
      (await axiosInstancev2.get(`${base(cohortId)}/assignments`)).data
        ?.assignments ?? [],
    { ...options, enabled: Boolean(cohortId) }
  );

export const useReminderLog = (cohortId, filters, offset = 0) => {
  const params = clean({
    assignment_id: filters.assignmentId,
    student_id: filters.studentId,
    kind: filters.kind,
    channel: filters.channel,
    status: filters.status,
    limit: LOG_PAGE_SIZE,
    offset,
  });
  return useQuery(
    [KEY, cohortId, 'log', params],
    async () =>
      (await axiosInstancev2.get(`${base(cohortId)}/log`, { params })).data,
    { ...options, enabled: Boolean(cohortId), keepPreviousData: true }
  );
};

export const remindersUnavailable = (error) => {
  const data = error?.response?.data;
  return error?.response?.status === 404 && !(data?.message || data?.error);
};

export const reminderError = (error, fallback) => {
  if (remindersUnavailable(error)) {
    return "Assignment reminders aren't available on the server yet.";
  }
  const data = error?.response?.data;
  return data?.message || data?.error || fallback;
};

// "due_48h" → 48; null for "missed".
export const kindHours = (kind) => {
  const match = /^due_(\d+)h$/.exec(kind || '');
  return match ? Number(match[1]) : null;
};

export const kindLabel = (kind) => {
  if (kind === 'missed') return 'Missed notice';
  const hours = kindHours(kind);
  return hours ? `${hours}h reminder` : kind;
};
